import { useState, useEffect } from 'preact/hooks'
import { integrationsApi } from '@/api/integrations'
import { IntegrationProvider, INTEGRATION_PROVIDER_LABELS } from '@/types/integrations'
import { needsAdminAttention } from '@/components/settings/integrations/crmSlot'

const BIGMARKER_FALLBACK_LABEL = INTEGRATION_PROVIDER_LABELS[IntegrationProvider.BigMarker] || 'BigMarker'

/**
 * Whether the workspace's company has BigMarker connected, and whether it is healthy.
 *
 * Reads the workspace-scoped summary rather than the company integrations list. The list is
 * company-admin only, so this used to 403 for every Editor on every campaign and web page — and
 * because a refusal took the same path as an empty response, BigMarker-dependent UI disappeared for
 * them with no explanation (ORB-699).
 *
 * `permissionDenied` exists so that never happens silently again: "we were not allowed to ask" is
 * not the same answer as "not connected", and a caller that hides an action should be able to tell
 * them apart even though both leave `hasBigMarker` false.
 */
export function useBigMarkerIntegration(workspaceId: string | undefined) {
  const [hasBigMarker, setHasBigMarker] = useState(false)
  const [integrationError, setIntegrationError] = useState(false)
  const [permissionDenied, setPermissionDenied] = useState(false)
  const [statusMessage, setStatusMessage] = useState<string | null>(null)
  const [integrationName, setIntegrationName] = useState<string>(BIGMARKER_FALLBACK_LABEL)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let mounted = true
    // Reset to loading state when the workspace changes to avoid stale flags
    setLoading(true)
    setHasBigMarker(false)
    setIntegrationError(false)
    setPermissionDenied(false)
    setStatusMessage(null)
    setIntegrationName(BIGMARKER_FALLBACK_LABEL)

    if (!workspaceId) {
      setLoading(false)
      return
    }

    async function check() {
      try {
        const res = await integrationsApi.getIntegrationSummary(workspaceId!)
        if (!mounted) return

        // Not thrown — the api client resolves a 403 rather than rejecting, so this branch is the
        // only place a refusal can be caught at all.
        if (res.status === 403) {
          setPermissionDenied(true)
          return
        }

        if (!res.data) return

        const bmConnection = res.data.find(
          c => c.Provider === IntegrationProvider.BigMarker
        )
        if (!bmConnection) return

        // Connection exists — hasBigMarker is true regardless of health
        setHasBigMarker(true)
        const isError = needsAdminAttention(bmConnection.Status)
        setIntegrationError(isError)
        setStatusMessage(isError ? (bmConnection.StatusMessage || null) : null)
        const providerLabel = INTEGRATION_PROVIDER_LABELS[bmConnection.Provider as IntegrationProvider]
        setIntegrationName(bmConnection.Name?.trim() || providerLabel || BIGMARKER_FALLBACK_LABEL)
      } catch (error) {
        console.warn('Failed to fetch BigMarker integration status:', error)
      } finally {
        if (mounted) setLoading(false)
      }
    }

    check()
    return () => { mounted = false }
  }, [workspaceId])

  return { hasBigMarker, integrationError, permissionDenied, integrationName, statusMessage, loading }
}
